import { apiRequest, API_BASE_URL, getToken } from './api';

export type TicketStatus = 'waiting_support' | 'waiting_user' | 'closed';
export interface Ticket {
  id: number;
  subject: string;
  status: TicketStatus;
  created_at: string;
  updated_at: string;
  owner?: { id: number; name: string; email: string } | null;
}
export interface TicketMessage {
  attachments?: TicketAttachment[];
  id: number;
  author_name: string;
  author_email?: string | null;
  is_staff: boolean | number;
  body: string;
  created_at: string;
}
export interface TicketAttachment { id: number; name: string; size: number; mime: string; }
export const isTicketImage = (file: TicketAttachment) => ['image/png', 'image/jpeg', 'image/webp'].includes(file.mime);
export async function loadTicketImage(ticketId: number, fileId: number, signal: AbortSignal): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/attachments/${fileId}/preview`, {
    headers: { Authorization: `Bearer ${getToken() ?? ''}` }, credentials: 'omit', signal,
  });
  if (!response.ok || !['image/png', 'image/jpeg', 'image/webp'].includes(response.headers.get('Content-Type')?.split(';')[0] ?? '')) {
    throw new Error('Не удалось открыть изображение.');
  }
  return response.blob();
}
const messageBody = (subject: string | null, body: string, files: File[]): RequestInit => {
  if (!files.length) return { method: 'POST', body: JSON.stringify(subject === null ? { body } : { subject, body }) };
  const form = new FormData();
  if (subject !== null) form.append('subject', subject);
  form.append('body', body);
  files.forEach(file => form.append('attachments[]', file));
  return { method: 'POST', body: form };
};
export async function downloadTicketAttachment(ticketId: number, file: TicketAttachment): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/attachments/${file.id}`, {
    headers: { Authorization: `Bearer ${getToken() ?? ''}`, Accept: 'application/octet-stream' }, credentials: 'omit',
  });
  if (!response.ok) throw new Error('Не удалось скачать вложение. Проверьте доступ и попробуйте ещё раз.');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url; link.download = file.name; document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}
export const ticketStatusLabel: Record<TicketStatus, string> = {
  waiting_support: 'Ожидает ответа поддержки', waiting_user: 'Ответ поддержки', closed: 'Закрыт',
};
export const ticketsAPI = {
  unread: () => apiRequest<{ success: boolean; unread: number }>('/tickets/unread'),
  markRead: (id: number, last_message_id: number) => apiRequest(`/tickets/${id}/read`, { method: 'POST', body: JSON.stringify({ last_message_id }) }),
  list: (scope: string, page = 1) => apiRequest<{ success: boolean; tickets: Ticket[]; page: number; has_more: boolean }>(`/tickets?scope=${scope}&page=${page}`),
  create: (subject: string, body: string, files: File[] = []) => apiRequest<{ success: boolean; ticket: Ticket }>('/tickets', messageBody(subject, body, files)),
  show: (id: number, before?: number) => apiRequest<{ success: boolean; ticket: Ticket; messages: TicketMessage[]; has_more: boolean }>(`/tickets/${id}${before ? `?before=${before}` : ''}`),
  reply: (id: number, body: string, files: File[] = []) => apiRequest(`/tickets/${id}/messages`, messageBody(null, body, files)),
  status: (id: number, status: 'closed' | 'waiting_support') => apiRequest(`/tickets/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};
